import { createSlice } from "@reduxjs/toolkit";
import type { RootState } from "../../app/store";
import { Context } from "./models/Context";
import { fetchContexts, switchContext, switchPortalContext } from "./slice-extensions/contexts-slice-extension";

type RequestStatus = "idle" | "loading" | "succeeded" | "failed";

interface ContextsState {
  contexts: Context[];
  /** The context the server remembers for the next sign-in. */
  activeKey: string | null;
  status: RequestStatus;
  /** The context (key) or portal member id being switched to; the page reloads on success. */
  switchingTo: string | null;
  switchFailed: boolean;
}

const initialState: ContextsState = {
  contexts: [],
  activeKey: null,
  status: "idle",
  switchingTo: null,
  switchFailed: false,
};

const contextsSlice = createSlice({
  name: "contexts",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchContexts.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchContexts.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.contexts = action.payload.contexts;
        state.activeKey = action.payload.activeKey;
      })
      .addCase(fetchContexts.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(switchContext.pending, (state, action) => {
        state.switchingTo = action.meta.arg.key;
        state.switchFailed = false;
      })
      .addCase(switchContext.rejected, (state) => {
        state.switchingTo = null;
        state.switchFailed = true;
      })
      .addCase(switchPortalContext.pending, (state, action) => {
        state.switchingTo = action.meta.arg.memberId;
        state.switchFailed = false;
      })
      .addCase(switchPortalContext.rejected, (state) => {
        state.switchingTo = null;
        state.switchFailed = true;
      });
  },
});

export const selectContexts = (state: RootState): Context[] => state.contexts.contexts;
export const selectActiveContextKey = (state: RootState): string | null => state.contexts.activeKey;
export const selectContextsStatus = (state: RootState): RequestStatus => state.contexts.status;
export const selectSwitchingTo = (state: RootState): string | null => state.contexts.switchingTo;
export const selectSwitchFailed = (state: RootState): boolean => state.contexts.switchFailed;

export default contextsSlice.reducer;
